// Preview renditions — the MP3 the hub's play button actually loads.
//
// A seed's audio is 16/24-bit PCM, tens of megabytes per object, and the
// Browse hub's <audio> had to pull that whole file over the network before a
// play button did anything. Encoding a 128 kbps MP3 here, once, at upload
// time makes the file that answers the click ten to twenty times smaller.
// Originals are untouched: Import and jams keep using the PCM.
//
// Encoded in the extension because it is the only place with the CPU for it:
// the Worker's isolate is killed on far less work than a LAME pass over a
// 20 MB stem (the 2026-08-19 AIFF-rewrap incident). LAME runs here as WASM
// (wasm-media-encoders) — about 70x realtime on a laptop, so a three-minute
// master costs a couple of seconds of an upload that already takes minutes.
//
// The encoder is stateful and instantiated once per process; `configure`
// resets it between objects. A failure mid-object throws that instance away
// so a corrupt state can never leak into the next preview.

import { createMp3Encoder, type WasmMediaEncoder } from "wasm-media-encoders";

// From shared, not from ./master-mix.js: that module reads files with node:fs,
// and this one has to stay Node-free so the viz app can encode previews for a
// project captured out of Audiotool in the browser.
import { decodeAudio, type DecodedAudio } from "../../../shared/audio/master-mix.js";

export const PREVIEW_BITRATE_KBPS = 128;
/** MPEG frames top out at 48 kHz; anything above is resampled by LAME. */
const MAX_MP3_RATE = 48000;
/** Frames handed to the encoder per call — a few thousand keeps the WASM
 * buffers small while still amortising the call overhead. */
const CHUNK_FRAMES = 16384;

export interface PreviewRendition {
  /** The pool hash of the original this previews (`previewOf` on the manifest). */
  previewOf: string;
  bytes: Uint8Array;
}

let encoderPromise: Promise<WasmMediaEncoder<"audio/mpeg">> | null = null;

function encoder(): Promise<WasmMediaEncoder<"audio/mpeg">> {
  encoderPromise ??= createMp3Encoder();
  return encoderPromise;
}

/** Chunks between yields: ~6 s of audio, a few tens of ms of LAME. A jam encodes
 * while Live is being polled and a browser while its page is in use. */
const CHUNKS_PER_YIELD = 16;

/** The instance is stateful and encoding yields, so callers take turns. */
let encoding: Promise<unknown> = Promise.resolve();

/**
 * Encode decoded PCM to MP3. Interleaved float input; anything wider than
 * stereo is folded down to stereo (a preview is for listening, not mixing).
 * Null when there is nothing to encode.
 */
export function encodeMp3Preview(pcm: DecodedAudio): Promise<Uint8Array | null> {
  const turn = encoding.then(() => encodeOne(pcm));
  encoding = turn.catch(() => undefined);
  return turn;
}

async function encodeOne(pcm: DecodedAudio): Promise<Uint8Array | null> {
  if (pcm.frames <= 0 || pcm.channels <= 0 || pcm.sampleRate <= 0) return null;
  const channels: 1 | 2 = pcm.channels === 1 ? 1 : 2;
  const planar = deinterleave(pcm, channels);

  const enc = await encoder();
  try {
    enc.configure({
      sampleRate: pcm.sampleRate,
      channels,
      bitrate: PREVIEW_BITRATE_KBPS,
      ...(pcm.sampleRate > MAX_MP3_RATE ? { outputSampleRate: MAX_MP3_RATE } : {}),
    });
    const parts: Uint8Array[] = [];
    let chunk = 0;
    for (let off = 0; off < pcm.frames; off += CHUNK_FRAMES) {
      const end = Math.min(off + CHUNK_FRAMES, pcm.frames);
      // The encoder hands back a view into its own buffer, reused on the next
      // call — copy before it is overwritten.
      parts.push(new Uint8Array(enc.encode(planar.map((ch) => ch.subarray(off, end)))));
      if (++chunk % CHUNKS_PER_YIELD === 0) await new Promise((resume) => setTimeout(resume, 0));
    }
    parts.push(new Uint8Array(enc.finalize()));
    return concat(parts);
  } catch (err) {
    // Whatever state the instance is in now, the next object must not see it.
    encoderPromise = null;
    throw err;
  }
}

/** Decode WAV/AIFF bytes (the shapes a seed stores) and encode a preview. */
export async function encodePreviewOf(bytes: Uint8Array): Promise<Uint8Array | null> {
  const pcm = decodeAudio(bytes);
  if (!pcm) return null;
  return encodeMp3Preview(pcm);
}

/**
 * A preview per original, in order. Best-effort by design: a preview that
 * fails to encode is logged and skipped — the seed still ships, that object
 * just plays its original — because a preview is a convenience and the
 * upload it rides on is minutes of someone's rendering.
 */
export async function encodePreviews(
  originals: ReadonlyArray<{ hash: string; bytes: Uint8Array }>,
): Promise<PreviewRendition[]> {
  const out: PreviewRendition[] = [];
  const seen = new Set<string>();
  for (const o of originals) {
    if (seen.has(o.hash)) continue;
    seen.add(o.hash);
    try {
      const bytes = await encodePreviewOf(o.bytes);
      if (bytes && bytes.byteLength > 0) out.push({ previewOf: o.hash, bytes });
      else console.warn(`[Seedbed] preview: nothing encodable in ${o.hash.slice(0, 8)} — skipped.`);
    } catch (err) {
      console.warn(`[Seedbed] preview: encoding ${o.hash.slice(0, 8)} failed — skipped:`, err);
    }
  }
  return out;
}

function deinterleave(pcm: DecodedAudio, channels: 1 | 2): Float32Array[] {
  const src = pcm.channels;
  const out = Array.from({ length: channels }, () => new Float32Array(pcm.frames));
  if (src === channels) {
    for (let f = 0; f < pcm.frames; f++) {
      for (let c = 0; c < channels; c++) out[c]![f] = pcm.samples[f * src + c]!;
    }
    return out;
  }
  if (src === 1) {
    // Mono to stereo never happens (mono stays mono), but keep the fold general.
    for (let f = 0; f < pcm.frames; f++) out[0]![f] = pcm.samples[f]!;
    if (channels === 2) out[1]!.set(out[0]!);
    return out;
  }
  // Wider than stereo: odd channels fold left, even fold right, equal-power-ish.
  const perSide = Math.ceil(src / 2);
  const gain = 1 / perSide;
  for (let f = 0; f < pcm.frames; f++) {
    let l = 0;
    let r = 0;
    for (let c = 0; c < src; c++) {
      const v = pcm.samples[f * src + c]!;
      if (c % 2 === 0) l += v;
      else r += v;
    }
    out[0]![f] = l * gain;
    if (channels === 2) out[1]![f] = r * gain;
  }
  return out;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.byteLength, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.byteLength;
  }
  return out;
}
